import { z } from "zod";
import { LocalDateSchema, PlanningOpaqueIdSchema, PlanningRequestIdSchema } from "./planning";

const ColorSchema = z.string().regex(/^#[0-9a-f]{6}$/u);
const InputColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/u);

export const AreaSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    name: z.string().min(1).max(120),
    child_copy: z.string().min(1).max(120),
    icon: z.string().max(80).nullable(),
    color: ColorSchema,
    sort_key: z.string().min(1).max(200),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
    archived_at: z.number().int().nullable(),
  })
  .strict();

export const AreaListResponseSchema = z
  .object({ items: z.array(AreaSchema).max(500), request_id: PlanningRequestIdSchema })
  .strict();

export const CreateAreaRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    child_copy: z.string().trim().min(1).max(120),
    icon: z.string().trim().max(80).nullable().default(null),
    color: InputColorSchema.default("#4f8f72"),
  })
  .strict();

export const UpdateAreaRequestSchema = CreateAreaRequestSchema.extend({
  expected_version: z.number().int().min(1),
}).strict();

export const AreaMutationResponseSchema = z
  .object({
    area: AreaSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const AreaArchiveCommandRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const AreaArchiveCommandResponseSchema = AreaMutationResponseSchema.extend({
  command: z.enum(["archive", "restore"]),
}).strict();

export const AreaReorderRequestSchema = z
  .object({
    items: z
      .array(
        z
          .object({ id: PlanningOpaqueIdSchema, expected_version: z.number().int().min(1) })
          .strict(),
      )
      .min(1)
      .max(500),
  })
  .strict()
  .refine((value) => new Set(value.items.map((item) => item.id)).size === value.items.length, {
    message: "area ids must be unique",
    path: ["items"],
  });

export const AreaReorderResponseSchema = z
  .object({
    items: z.array(AreaSchema).min(1).max(500),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const TermSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    name: z.string().min(1).max(120),
    school_year_label: z.string().max(80).nullable(),
    start_local_date: LocalDateSchema,
    end_local_date: LocalDateSchema,
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
    archived_at: z.number().int().nullable(),
  })
  .strict();

export const TermListResponseSchema = z
  .object({ items: z.array(TermSchema).max(500), request_id: PlanningRequestIdSchema })
  .strict();

export const CreateTermRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    school_year_label: z.string().trim().max(80).nullable().default(null),
    start_local_date: LocalDateSchema,
    end_local_date: LocalDateSchema,
  })
  .strict()
  .refine((value) => value.end_local_date >= value.start_local_date, {
    message: "end_local_date must not be earlier than start_local_date",
    path: ["end_local_date"],
  });

export const UpdateTermRequestSchema = CreateTermRequestSchema.extend({
  expected_version: z.number().int().min(1),
}).strict();

export const TermMutationResponseSchema = z
  .object({
    term: TermSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const TermArchiveCommandRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const TermArchiveCommandResponseSchema = TermMutationResponseSchema.extend({
  command: z.enum(["archive", "restore"]),
}).strict();
